import React, { useEffect, useState } from 'react';
import { t } from '@citrite/translate';
import { layout, LightText, palette, typography } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import {
	getDayTime,
	getGreetingRefreshTimeInMilliseconds,
	getTodayString,
} from 'App/Header/util';
import { useIsDetailedDesktopTileEnabled } from 'Configuration/hooks';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { centerContentWidthPX, hideGreetingsBreakpoint } from 'Workspace/dimensions';
import { useUserContext } from 'Workspace/UserContext';

const StyledDivLegacy = styled.div`
	display: flex;
	flex-direction: column;
	padding: ${layout.space32} ${layout.space64} 0 ${layout.space64};
	background-color: inherit;
`;

const StyledDivDetailed = styled(StyledDivLegacy)`
	max-width: ${centerContentWidthPX}px;
	padding: ${layout.space32} ${layout.space20} 0 ${layout.space20};
	margin: 0 auto;
	overflow: hidden;
	max-height: 200px;
	opacity: 1;
	transform: translateY(0);
	transition: max-height 250ms ease, opacity 250ms ease, transform 250ms ease,
		padding-top 250ms ease;

	@media (max-height: ${hideGreetingsBreakpoint}) {
		max-height: 0;
		opacity: 0;
		transform: translateY(-6px);
		padding-top: 0;
		pointer-events: none;
	}
`;

const StyledMessageSpan = styled.span`
	font-size: ${typography.sizes.xl2};
	font-weight: ${typography.weights.semiBold};
	color: ${palette.black};
`;

const StyledLightText = styled(LightText)`
	font-weight: ${typography.weights.regular};
	margin-top: ${layout.tinySpace};
	color: ${palette.grey800};
`;

export const Greeting = () => {
	const [greetingDay, setGreetingDay] = useState<String>('');
	const [greetingMessage, setGreetingMessage] = useState<String>('');
	const [greetingRefreshTimeout, setGreetingRefreshTimeout] =
		useState<ReturnType<typeof setTimeout>>();
	const { userDetails } = useUserContext();
	const showUsername = useFeatureCanary(FeatureFlag.EnableUserDisplayName);
	const isDetailedDesktopTileEnabled = useIsDetailedDesktopTileEnabled();
	const setGreeting = () => {
		const username =
			showUsername && userDetails?.isValidUserDisplayName && userDetails?.userDisplayName
				? `, ${userDetails.userDisplayName}`
				: '';
		setGreetingDay(getTodayString());
		setGreetingMessage(`${t(`Workspace:greeting.${getDayTime()}`)}${username}`);
		if (greetingRefreshTimeout) {
			clearTimeout(greetingRefreshTimeout);
		}
		const refreshTime = getGreetingRefreshTimeInMilliseconds();
		if (refreshTime > 0) {
			setGreetingRefreshTimeout(setTimeout(setGreeting, refreshTime));
		}
	};

	useEffect(() => {
		setGreeting();
		return () => {
			clearTimeout(greetingRefreshTimeout);
		};
	}, [userDetails]); // eslint-disable-line react-hooks/exhaustive-deps

	const StyledDiv = isDetailedDesktopTileEnabled ? StyledDivDetailed : StyledDivLegacy;

	return (
		<StyledDiv data-testid="application-greeting">
			<StyledMessageSpan data-testid="greeting-message">
				{greetingMessage}
			</StyledMessageSpan>
			<StyledLightText data-testid="greeting-day">{greetingDay}</StyledLightText>
		</StyledDiv>
	);
};
